import { memo } from "react";
import PropTypes from "prop-types";

/** Function to get the glyph spec for the program alert row.
 * @param {"info" | "warning" | "success"} variant
 * @returns {{ iconClass: string, glyphKind: "text" | "checkMask", glyph?: string }}
 */
function programAlertGlyphSpec(variant) {
  if (variant === "warning") {
    return {
      iconClass: "ProgramInsights-volumeAlert-glyph--warning",
      glyphKind: "text",
      glyph: "!",
    };
  }
  if (variant === "success") {
    return {
      iconClass: "ProgramInsights-volumeAlert-glyph--success",
      glyphKind: "checkMask",
    };
  }
  return {
    iconClass: "ProgramInsights-volumeAlert-glyph--info",
    glyphKind: "text",
    glyph: "i",
  };
}

const ALERT_ROOT_TAGS = ["li", "aside", "div"];

/**
 * Shared Program Insights alert row (sleep / energy / mood tokens): volume & coverage list,
 * per-lens week summary footers (Olympic / Strongman / Streetlifting unprogrammed lifts), etc.
 *
 * @param {object} props
 * @param {"info" | "warning" | "success"} props.variant
 * @param {"li" | "aside" | "div"} [props.as] — DOM tag for the row root (default `div`).
 */
function InsightsProgramAlertRow({ as = "div", variant, children, className = "", ...rest }) {
  const Tag = as;
  const glyphSpec = programAlertGlyphSpec(variant);
  const rootClass = [
    "ProgramInsights-volumeAlert",
    `ProgramInsights-volumeAlert--${variant}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Tag className={rootClass} {...rest}>
      <span
        className={`ProgramInsights-volumeAlert-glyph ${glyphSpec.iconClass}`}
        aria-hidden="true"
      >
        {glyphSpec.glyphKind === "checkMask" ? (
          <span className="ProgramInsights-volumeAlert-checkIcon" />
        ) : (
          glyphSpec.glyph
        )}
      </span>
      <span className="ProgramInsights-volumeAlert-text">{children}</span>
    </Tag>
  );
}

InsightsProgramAlertRow.propTypes = {
  as: PropTypes.oneOf(ALERT_ROOT_TAGS),
  variant: PropTypes.oneOf(["info", "warning", "success"]).isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export default memo(InsightsProgramAlertRow);
